import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons'
import {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent,
} from 'react'
import classes from '../../styles/crmIntegrationChat.module.css'

type CrmIntegrationVoicePlayerProps = {
  src: string
  durationSec?: number | null
  waveform?: number[] | null
  onPlaybackError?: () => void
}

const formatPlayerTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const totalSec = Math.floor(seconds)
  const minutes = Math.floor(totalSec / 60)
  const secs = totalSec % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

const CrmIntegrationVoicePlayer: FC<CrmIntegrationVoicePlayerProps> = ({
  src,
  durationSec,
  waveform,
  onPlaybackError,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(
    typeof durationSec === 'number' && Number.isFinite(durationSec)
      ? durationSec
      : 0,
  )
  const [isSeeking, setIsSeeking] = useState(false)

  const hasWaveform = Boolean(waveform?.length)

  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(
      typeof durationSec === 'number' && Number.isFinite(durationSec)
        ? durationSec
        : 0,
    )
  }, [durationSec, src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime)
      }
    }

    const handleLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
      audio.currentTime = 0
    }

    const handleError = () => {
      setIsPlaying(false)
      onPlaybackError?.()
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [isSeeking, onPlaybackError, src])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }

    void audio
      .play()
      .then(() => {
        setIsPlaying(true)
      })
      .catch(() => {
        setIsPlaying(false)
        onPlaybackError?.()
      })
  }, [isPlaying, onPlaybackError])

  const handleSeekChange = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio || duration <= 0) {
      return
    }

    const nextPercent = Number(event.target.value)
    const nextTime = (nextPercent / 100) * duration
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const handleSeekPointerDown = (event: PointerEvent<HTMLInputElement>) => {
    event.stopPropagation()
    setIsSeeking(true)
  }

  const handleSeekPointerUp = () => {
    setIsSeeking(false)
  }

  const progressPercent =
    duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0
  const displaySeconds =
    isPlaying || currentTime > 0 ? currentTime : duration

  return (
    <div className={classes.voicePlayer}>
      <button
        type="button"
        className={classes.voicePlayerPlayButton}
        aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
        onClick={togglePlay}
      >
        {isPlaying ? (
          <PauseOutlined
            className={classes.voicePlayerPlayIcon}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ) : (
          <CaretRightOutlined
            className={classes.voicePlayerPlayIcon}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        )}
      </button>

      {hasWaveform ? (
        <div className={classes.voiceWaveform} aria-hidden>
          {waveform?.map((value, index) => (
            <span
              key={index}
              className={classes.voiceWaveformBar}
              style={{
                height: `${Math.max(12, Math.round((value / 31) * 100))}%`,
              }}
            />
          ))}
        </div>
      ) : (
        <input
          type="range"
          className={classes.voicePlayerRange}
          min={0}
          max={100}
          step={0.1}
          value={progressPercent}
          disabled={duration <= 0}
          aria-label="Позиция воспроизведения"
          style={{
            background: `linear-gradient(to right, #835499 ${progressPercent}%, #e8e8e8 ${progressPercent}%)`,
          }}
          onChange={handleSeekChange}
          onPointerDown={handleSeekPointerDown}
          onPointerUp={handleSeekPointerUp}
          onPointerLeave={handleSeekPointerUp}
          onClick={(event) => event.stopPropagation()}
        />
      )}

      <span className={classes.voicePlayerTime}>
        {formatPlayerTime(displaySeconds)}
      </span>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className={classes.voicePlayerAudio}
      />
    </div>
  )
}

export default CrmIntegrationVoicePlayer
